import { Component } from '@theme/component';

/**
 * Segmented toggle that swaps between the "Custom Stickers" and "Premade Stickers" comparison
 * panels, with a sliding pill that mirrors the active tab's position/width.
 *
 * @typedef {object} ComparisonToggleRefs
 * @property {HTMLButtonElement[]} tab
 * @property {HTMLElement[]} panel
 * @property {HTMLElement} panelWrap
 * @property {HTMLElement} pill
 *
 * @extends {Component<ComparisonToggleRefs>}
 */
export class ComparisonToggleComponent extends Component {
  requiredRefs = ['tab', 'panel', 'pill'];

  connectedCallback() {
    super.connectedCallback();
    this.#positionPill();
    this.resizeObserver = new ResizeObserver(() => this.#positionPill());
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  /**
   * @param {{ panel: 'custom' | 'premade' }} data
   */
  selectPanel({ panel }) {
    const index = panel === 'premade' ? 1 : 0;

    this.refs.tab.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
    });

    this.refs.panel.forEach((panelEl, i) => {
      panelEl.hidden = i !== index;
    });

    this.#positionPill();
  }

  #positionPill() {
    const activeTab = this.refs.tab.find((tab) => tab.getAttribute('aria-selected') === 'true') ?? this.refs.tab[0];
    if (!activeTab) return;

    const { pill } = this.refs;
    const wrapLeft = activeTab.parentElement?.getBoundingClientRect().left ?? 0;
    const { left, width } = activeTab.getBoundingClientRect();

    pill.style.width = `${width}px`;
    pill.style.transform = `translateX(${left - wrapLeft}px)`;
  }
}

if (!customElements.get('comparison-toggle-component')) {
  customElements.define('comparison-toggle-component', ComparisonToggleComponent);
}
